/**
 * View script for the block.
 */

/**
 * visual4 mega menu – view.js (display:none + open/close animations)
 *
 * Needs CSS states on the top-level item:
 * - .is-open     -> panel visible (display:block, opacity:1, transform:0, visibility:visible)
 * - .is-closing  -> panel animating out (display:block, opacity:0, transform:-20px, visibility:hidden w/ delay)
 * - .is-opening  -> panel rendered but still "closed" for 1 frame (display:block, opacity:0, transform:-20px)
 *
 * Panel element: :scope > .wp-block-visual4-mega-menu__submenu
 */
( function () {
    const navRoot = document.querySelector( '.wp-block-visual4-mega-menu' );
    if ( ! navRoot ) return;

    navRoot.classList.add( 'is-js-enabled' );

    const container = navRoot.querySelector( '.wp-block-visual4-mega-menu__container' );
    if ( ! container ) return;

    const topLevelSelector = 'li.menu-item-has-children';
    const toggleSelector = '.wp-block-visual4-mega-menu__submenu-toggle';
    const closeSelector = '.wp-block-visual4-mega-menu__submenu-close';
    const panelSelector = ':scope > .wp-block-visual4-mega-menu__submenu';

    const openClass = 'is-open';
    const closingClass = 'is-closing';
    const openingClass = 'is-opening';

    const topLevelItems = Array.from( container.children ).filter( ( node ) => {
        return node.nodeType === 1 && node.matches( topLevelSelector );
    } );

    function isTopLevelItem( element ) {
        return element && element.matches( topLevelSelector ) && element.parentElement === container;
    }

    function getTopLevelItemFrom( element ) {
        const candidate = element.closest( topLevelSelector );
        return isTopLevelItem( candidate ) ? candidate : null;
    }

    function getToggleButton( menuItem ) {
        return menuItem.querySelector( toggleSelector );
    }

    function getPanel( menuItem ) {
        return menuItem.querySelector( panelSelector );
    }

    function setAriaExpanded( menuItem, expanded ) {
        getToggleButton( menuItem )?.setAttribute( 'aria-expanded', expanded ? 'true' : 'false' );
    }

    function closeAllExcept( exceptItem = null ) {
        topLevelItems.forEach( ( menuItem ) => {
            if ( menuItem !== exceptItem ) closeMenuItem( menuItem );
        } );
    }

    function isOpen( menuItem ) {
        return menuItem.classList.contains( openClass );
    }

    function openMenuItem( menuItem ) {
        closeAllExcept( menuItem );

        // Cancel any in-progress close.
        menuItem.classList.remove( closingClass );

        // Ensure we have a rendered "start frame" for the transition.
        menuItem.classList.add( openingClass );
        menuItem.classList.remove( openClass );
        setAriaExpanded( menuItem, true );

        // Force layout so opening state is committed.
        void getPanel( menuItem )?.offsetHeight;

        // Next frame: switch to open -> transition runs.
        requestAnimationFrame( () => {
            menuItem.classList.add( openClass );
            menuItem.classList.remove( openingClass );
        } );
    }

    function closeMenuItem( menuItem ) {
        const panel = getPanel( menuItem );

        // If opening is in progress, cancel it.
        menuItem.classList.remove( openingClass );

        menuItem.classList.remove( openClass );
        menuItem.classList.add( closingClass );
        setAriaExpanded( menuItem, false );

        if ( ! panel ) {
            menuItem.classList.remove( closingClass );
            return;
        }

        const onEnd = ( event ) => {
            if ( event.target !== panel ) return;
            panel.removeEventListener( 'transitionend', onEnd );

            // If reopened mid-animation, don't clear closing state here.
            if ( menuItem.classList.contains( openClass ) ) return;

            menuItem.classList.remove( closingClass );
        };

        panel.addEventListener( 'transitionend', onEnd );
    }

    // Init toggles.
    topLevelItems.forEach( ( menuItem ) => {
        const toggleButton = getToggleButton( menuItem );
        if ( ! toggleButton ) return;

        toggleButton.type = 'button';

        if ( ! toggleButton.hasAttribute( 'aria-expanded' ) ) {
            toggleButton.setAttribute( 'aria-expanded', 'false' );
        }

        const open = toggleButton.getAttribute( 'aria-expanded' ) === 'true';

        menuItem.classList.toggle( openClass, open );
        menuItem.classList.remove( openingClass, closingClass );
        setAriaExpanded( menuItem, open );
    } );

    // Hover open/close (keeps aria-expanded aligned with hover UX).
    topLevelItems.forEach( ( menuItem ) => {
        menuItem.addEventListener( 'mouseenter', () => openMenuItem( menuItem ) );
        menuItem.addEventListener( 'mouseleave', () => closeMenuItem( menuItem ) );
    } );

    function updateMegaMenuAnchorY() {
        const anchorItem = container.querySelector(':scope > li');
        if (!anchorItem) return;

        navRoot.style.setProperty(
            '--v4--mega-menu--anchor-y',
            `${Math.round(anchorItem.getBoundingClientRect().bottom)}px`
        );
    }

    updateMegaMenuAnchorY();
    window.addEventListener('resize', updateMegaMenuAnchorY);
    window.addEventListener('scroll', updateMegaMenuAnchorY, { passive: true });

    // Click toggle.
    navRoot.addEventListener( 'click', ( event ) => {
        const toggleButton = event.target.closest( toggleSelector );
        if ( ! toggleButton ) return;

        const menuItem = getTopLevelItemFrom( toggleButton );
        if ( ! menuItem ) return;

        event.preventDefault();

        if ( isOpen( menuItem ) ) closeMenuItem( menuItem );
        else openMenuItem( menuItem );
    } );

    // Click close button inside panel.
    navRoot.addEventListener( 'click', ( event ) => {
        const closeButton = event.target.closest( closeSelector );
        if ( ! closeButton ) return;

        const menuItem = getTopLevelItemFrom( closeButton );
        if ( ! menuItem ) return;

        event.preventDefault();

        closeMenuItem( menuItem );
        getToggleButton( menuItem )?.focus();
    } );

    // Escape closes the currently open item.
    document.addEventListener( 'keydown', ( event ) => {
        if ( event.key !== 'Escape' ) return;

        const openItem = topLevelItems.find( ( menuItem ) => menuItem.classList.contains( openClass ) );
        if ( ! openItem ) return;

        closeMenuItem( openItem );
        getToggleButton( openItem )?.focus();

        event.preventDefault();
    } );

    // Click outside closes all.
    document.addEventListener( 'click', ( event ) => {
        if ( navRoot.contains( event.target ) ) return;
        topLevelItems.forEach( ( menuItem ) => closeMenuItem( menuItem ) );
    } );
} )();